/**
 * Iter78 — shared style/format helpers used across slide-preview
 * layouts. Extracted from the monolithic `SlidePreviewBody.jsx` so
 * per-layout components can import a stable, side-effect-free API
 * without re-implementing colour math or truncation heuristics.
 *
 * Nothing here touches React state — every helper is a pure function
 * (or a stateless SVG component in the case of `UserGlyph`).
 */

/** Shorten a ring value so it fits inside a small circular overlay.
 *  Strips leading tildes/spaces and truncates at a word boundary
 *  after ~22 chars. Values ≤ 22 chars pass through untouched. */
export function shortRingValue(v) {
  if (!v) return "";
  const stripped = v.trim().replace(/^~\s*/, "");
  if (stripped.length <= 22) return stripped;
  const cut = stripped.slice(0, 22);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 12 ? cut.slice(0, lastSpace) : cut) + "…";
}

/** Darken a hex color by blending toward black.
 *  `ratio` in [0, 1] — 0 = identity, 1 = full black.
 *  Accepts `#RGB` or `#RRGGBB`. Returns the input unchanged on parse
 *  failure so downstream callers never see `undefined`. */
export function darkenHex(hex, ratio = 0.4) {
  if (!hex || typeof hex !== "string" || hex[0] !== "#") return hex;
  let s = hex.slice(1);
  if (s.length === 3) s = s.split("").map((c) => c + c).join("");
  if (s.length !== 6) return hex;
  const n = parseInt(s, 16);
  if (Number.isNaN(n)) return hex;
  const r = Math.round(((n >> 16) & 0xff) * (1 - ratio));
  const g = Math.round(((n >> 8) & 0xff) * (1 - ratio));
  const b = Math.round((n & 0xff) * (1 - ratio));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

/** Resolve the top-bar accent colour for a slide's narrative pillar.
 *  Falls back to the brand primary when the slide isn't tagged. */
export function pillarAccentColor(pillar, primary) {
  const p = (pillar || "").toUpperCase();
  if (p === "P2") return darkenHex(primary, 0.30);
  if (p === "P3") return "#F59E0B";
  return primary;
}

/** Adaptive bullet spacing (iter69). Given a bullet array the caller
 *  wants to render, return the flex `justify` class + a gap class that
 *  reads as intentional design regardless of count:
 *   • 1 short bullet (≤ 8 words) → hero one-liner, vertically centred
 *   • 1-5 bullets otherwise → top-aligned, count-scaled gap
 *   • 6+ bullets → historical `justify-around` so dense lists stay tight
 *  Returns `{ justify, gap, gap2, isHeroOneLiner, isDense, isSparse }`. */
export function computeBulletSpacing(bullets) {
  const nonEmpty = (bullets || []).filter((b) => b && b.trim());
  const count = nonEmpty.length;
  const firstWords = count === 1 ? nonEmpty[0].trim().split(/\s+/).length : 0;
  const isHeroOneLiner = count === 1 && firstWords <= 8;
  const isDense = count >= 6;
  let justify;
  let gap;
  let gap2;
  if (isHeroOneLiner) {
    justify = "justify-center";
    gap = "gap-5";
    gap2 = "gap-5";
  } else if (isDense) {
    justify = "justify-around";
    gap = "gap-1.5";
    gap2 = "gap-2";
  } else if (count <= 2) {
    justify = "justify-start"; gap = "gap-5"; gap2 = "gap-5";
  } else if (count === 3) {
    justify = "justify-start"; gap = "gap-4"; gap2 = "gap-4";
  } else {
    justify = "justify-start"; gap = "gap-3"; gap2 = "gap-3";
  }
  return { justify, gap, gap2, isHeroOneLiner, isDense, isSparse: !isDense };
}

/** Trim a bullet to `cap` words, appending "…" when trimmed. Used by
 *  the Capital Ask preview and any layout that needs a hard word cap. */
export function trimBulletWords(s, cap = 12) {
  if (!s) return "";
  const w = s.trim().split(/\s+/);
  if (w.length <= cap) return s.trim();
  return w.slice(0, cap).join(" ").replace(/[,.;:\-—]+$/, "") + "…";
}

/** Format a raw currency amount for compact display: 45000000 → "45.0M",
 *  2500 → "3k" (rounded), <=0 → em-dash. */
export function formatCurrencyShort(n) {
  const v = Number(n);
  if (!v || !Number.isFinite(v)) return "\u2014";
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
  return String(Math.round(v));
}

/** Format a percentage for pill display. Integers render as "35%",
 *  fractions as "35.5%". Non-finite / non-positive → null (caller
 *  decides whether to render or skip the pill). */
export function formatPct(p) {
  if (!Number.isFinite(p) || p <= 0) return null;
  return Number.isInteger(p) ? `${p}%` : `${p.toFixed(1)}%`;
}

/** Simple "person" glyph used inside the Our Team empty-state cards.
 *  Rolled as inline SVG so it scales fluidly with the parent disc
 *  without a Phosphor-specific weight dependency. */
export function UserGlyph({ size = "60%", ink = "#FFFFFF" }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}
      style={{ display: "block" }} aria-hidden="true">
      <circle cx="50" cy="36" r="17" fill={ink} />
      <path d="M18,86 Q18,58 50,58 Q82,58 82,86 Z" fill={ink} />
    </svg>
  );
}
